/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ChevronLeftIcon } from '@radix-ui/react-icons';
import { Button, Skeleton, Text, Title } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';
import { formatLocalizedDate } from '@/shared/utils/date';
import type { Deal } from '@/services/requests/housien-services/DealsService';
import { useAgencyProperties } from '@/features/visits/hooks/useAgencyProperties';
import type { DealPartyNames } from '../hooks/useDealPartyNames';
import { useDealStageLabels, type DealStage } from '../constants/deal-stages';
import type { DealViewState } from '../utils/deal-state';

interface DealDetailHeaderProps {
  agencyId: string;
  deal: Deal;
  names: DealPartyNames;
  /** True while the deal still runs — the only time property is editable. */
  isRunning: boolean;
  onDefineProperty: () => void;
  onEdit: () => void;
  /** The deal's derived view state, driving the stage chip. */
  view: DealViewState;
}

/**
 * Initials shown in an avatar circle.
 */
function initialsOf(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .map((word) => word[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

/**
 * The capture channels the backend can emit (`ClientOrigin.VALID_CHANNELS`).
 */
const KNOWN_ORIGIN_CHANNELS: readonly string[] = [
  'site', 'portal_olx', 'portal_grupo_olx', 'meta', 'referral', 'manual',
];

/**
 * The origin chip label key for a deal's capture channel. An unrecognized
 * channel falls back to a generic key rather than the raw wire value,
 * because the wire never carries prose (rule `no-prose-from-wire`).
 *
 * @param deal - The deal whose origin is being labeled.
 * @returns The `features/deals` key for the chip.
 */
function originKeyOf(deal: Deal): string {
  const channel = deal.origin?.channel;
  if (!channel || !KNOWN_ORIGIN_CHANNELS.includes(channel)) {
    return 'originChannels.unknown';
  }
  return `originChannels.${channel}`;
}

/** Neutral chip: what the attendance is, not how it is going. */
const NEUTRAL_CHIP =
  'inline-flex h-[22px] items-center whitespace-nowrap rounded-(--radius-full) bg-(--surface-tertiary) px-[9px] text-[length:var(--text-meta)] [font-weight:var(--font-semibold)] text-(--text-tertiary)';

/**
 * Tint for the stage chip, by lifecycle. A running attendance reads as
 * information, a won one as success, a lost one as an error, and a
 * paused one stays neutral — it is not going badly, it is not going.
 *
 * @param view - The deal's derived view state.
 * @returns The chip's background and text classes.
 */
function stageChipClass(view: DealViewState): string {
  if (view.isWon) return 'bg-(--status-success-bg) text-(--status-success-text)';
  if (view.isLost) return 'bg-(--status-error-bg) text-(--status-error-text)';
  if (view.lifecycle === 'paused') return 'bg-(--surface-tertiary) text-(--text-secondary)';
  return 'bg-(--status-info-bg) text-(--status-info-text)';
}

/**
 * Property line under the client name: a link when the property is set,
 * the "not defined yet" note plus the define affordance while it is not.
 */
function PropertyLine({ agencyId, deal, names, isRunning, onDefineProperty }: DealDetailHeaderProps) {
  const { t } = useTranslation('features/deals');
  const { properties } = useAgencyProperties(agencyId);
  if (deal.property_id) {
    // Warn before the broker acts, not after. The backend already
    // refuses an offer on a closed property, but until this the refusal
    // arrived only once the form had been filled in and submitted.
    const status = properties
      .find((p) => p.id === deal.property_id)?.raw?.classification?.status;
    const closed = status === 'sold' || status === 'rented';
    return (
      <>
        <Link
          to={`/app/agencies/${agencyId}/properties/${deal.property_id}`}
          className="text-[length:var(--text-body-sm)] [font-weight:var(--font-semibold)] text-(--seed-base) hover:opacity-80 no-underline"
        >
          {names.propertyResolving
            ? <NamePlaceholder width={140} height={14} />
            : names.propertyName ?? deal.property_id}
        </Link>
        {closed && (
          <Text
            component="span"
            data-testid="property-closed-badge"
            className={cn(
              'rounded-full border border-(--accent-cobre)/40 bg-(--accent-cobre)/10',
              'px-(--space-2) py-(--space-1) text-(--accent-cobre)',
              'text-[length:var(--text-tag)] uppercase',
              'tracking-(--tracking-chip)',
            )}
          >
            {t(`propertyClosed.${status}`)}
          </Text>
        )}
      </>
    );
  }
  return (
    <>
      <Text component="span" className="text-[length:var(--text-body-sm)] text-(--text-secondary)">
        {t('detail.header.noProperty')}
      </Text>
      {isRunning && (
        <Button
          variant="ghost-text"
          size="micro"
          onClick={onDefineProperty}
          data-testid="define-property-button"
        >
          {t('detail.header.defineProperty')}
        </Button>
      )}
    </>
  );
}

/**
 * Resolves the client's display name, falling back to the raw id or a
 * "no client" label — the label shown in both the breadcrumb and the
 * header title, so the two never disagree.
 */
function clientLabelOf(deal: Deal, names: DealPartyNames, t: (key: string) => string): string {
  return names.clientName ?? deal.client_id ?? t('detail.header.noClient');
}

/**
 * Placeholder held in a name's place while the record behind the id is
 * still loading. The raw id used to sit here for that beat, so the page
 * opened on a uuid and only then became a person.
 */
function NamePlaceholder({ width, height }: { width: number; height: number }) {
  return (
    <Skeleton
      data-testid="deal-name-placeholder"
      width={width}
      height={height}
      radius="var(--radius-sm)"
      className="inline-block align-middle"
    />
  );
}

/**
 * Breadcrumb row linking back to the deals list from the detail view, in
 * the same "list / current item" shape properties and clients use.
 */
export function DealDetailBreadcrumb({ deal, names, onBack }: {
  deal: Deal;
  names: DealPartyNames;
  onBack: () => void;
}) {
  const { t } = useTranslation('features/deals');
  const clientLabel = clientLabelOf(deal, names, t);
  return (
    <div className="flex items-center gap-(--space-2) text-[length:var(--text-meta)] text-(--text-tertiary-soft)">
      <Button
        variant="none"
        onClick={onBack}
        className={cn(
          'flex items-center gap-(--space-1) border-none bg-transparent p-0',
          'text-[length:var(--text-meta)] [font-weight:var(--font-semibold)] text-(--seed-base) cursor-pointer',
        )}
        data-testid="back-to-deals"
      >
        <ChevronLeftIcon width={14} height={14} /> {t('detail.backToList')}
      </Button>
      <Text as="span">/</Text>
      <Text as="span" className="truncate">
        {names.clientResolving ? <NamePlaceholder width={110} height={11} /> : clientLabel}
      </Text>
    </div>
  );
}

/**
 * The broker block on the right of the header: who owns this attendance.
 */
function ResponsibleBlock({ brokerLabel }: { brokerLabel: string }) {
  const { t } = useTranslation('features/deals');
  return (
    // Ruled off from the Editar button: who owns the attendance is an
    // attribute of the record, not another thing to press, and without
    // the rule the two read as one cluster of controls.
    <div className="flex items-center gap-(--space-2-5) border-l border-(--border-subtle) pl-(--space-3)">
      <div className="flex flex-col items-end">
        <Text component="span" className="text-[length:var(--text-meta)] text-(--text-tertiary-soft)">
          {t('detail.header.responsible')}
        </Text>
        <Text
          component="span"
          className="whitespace-nowrap text-[length:var(--text-body-sm)] [font-weight:var(--font-semibold)] text-(--text-primary)"
        >
          {brokerLabel}
        </Text>
      </div>
      <div className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-(--accent-petroleo) text-[length:var(--text-meta)] [font-weight:var(--font-bold)] text-(--text-inverse)">
        {initialsOf(brokerLabel)}
      </div>
    </div>
  );
}

/**
 * Detail header: the client's initials, the state chips, their name, the
 * property line, and the responsible broker — on the page ground rather
 * than in a card, so the status band below it is the first panel the eye
 * lands on.
 *
 * @param props - The deal, its resolved party names, and callbacks.
 * @returns The header block.
 */
export function DealDetailHeader(props: DealDetailHeaderProps) {
  const { agencyId, deal, names, view } = props;
  const { t } = useTranslation('features/deals');
  const labels = useDealStageLabels();
  const clientLabel = clientLabelOf(deal, names, t);
  const brokerLabel = names.brokerName ?? deal.broker_id;

  return (
    <div data-testid="deal-detail-header" className="flex flex-wrap items-start gap-(--space-4)">
      {/* Centred against the whole name block rather than top-aligned:
          at 56px the avatar is taller than the chip row it would
          otherwise line up with, and it read as hanging off the top. */}
      <div className="flex h-[56px] w-[56px] flex-none items-center justify-center self-center rounded-full bg-(--accent-ameixa) text-[length:var(--text-app-section)] [font-weight:var(--font-bold)] text-(--text-inverse)">
        {names.clientResolving ? '' : initialsOf(clientLabel)}
      </div>

      <div className="flex min-w-0 flex-[1_1_20rem] flex-col gap-(--space-1-5)">
        <div className="flex flex-wrap items-center gap-(--space-2)">
          <Text
            component="span"
            data-testid="deal-stage-chip"
            className={cn(
              'inline-flex h-[22px] items-center whitespace-nowrap rounded-(--radius-full) px-[9px]',
              'text-[length:var(--text-meta)] [font-weight:var(--font-semibold)]',
              stageChipClass(view),
            )}
          >
            {labels[view.displayStage as DealStage] ?? view.displayStage}
          </Text>
          {/*
            An attendance opened from a visit is born typeless on purpose
            — a booking reveals neither purchase nor rental. The chip used
            to fall through to "Aluguel" for those, labelling a decision
            nobody had made yet, so a missing type now shows no chip.
          */}
          {deal.deal_type && (
            <Text component="span" data-testid="deal-type-chip" className={NEUTRAL_CHIP}>
              {deal.deal_type === 'sale' ? t('types.sale') : t('types.rental')}
            </Text>
          )}
          <Text component="span" className={NEUTRAL_CHIP}>{t(originKeyOf(deal))}</Text>
        </div>

        <Title
          order={1}
          style={{ fontSize: 'var(--text-app-title)', fontWeight: 'var(--font-bold)' }}
          className="m-0 leading-[var(--leading-tight)] tracking-[var(--tracking-app-title)] text-(--text-primary)"
        >
          {deal.client_id ? (
            <Link
              to={`/app/agencies/${agencyId}/clients/${deal.client_id}`}
              className="text-(--text-primary) no-underline hover:text-(--seed-base)"
            >
              {names.clientResolving ? <NamePlaceholder width={220} height={22} /> : clientLabel}
            </Link>
          ) : clientLabel}
        </Title>

        <div className="flex flex-wrap items-center gap-(--space-2) text-[length:var(--text-body-sm)]">
          <PropertyLine {...props} />
          <Text component="span" className="text-(--text-tertiary-soft)">·</Text>
          <Text component="span" className="text-(--text-secondary)">
            {t('detail.header.openedOn', { date: formatLocalizedDate(deal.created_at) })}
          </Text>
        </div>
      </div>

      <div className="flex flex-none flex-wrap items-center gap-(--space-3)">
        {props.isRunning && (
          <Button
            variant="none"
            size="custom"
            onClick={props.onEdit}
            data-testid="edit-attendance-button"
            className="h-[38px] whitespace-nowrap rounded-(--radius-md) border border-solid border-(--border-medium) bg-(--surface-primary) px-(--space-4) text-[length:var(--text-body-sm)] [font-weight:var(--font-semibold)] text-(--text-primary) hover:bg-(--surface-hover)"
          >
            {t('detail.header.edit')}
          </Button>
        )}
        <ResponsibleBlock brokerLabel={brokerLabel} />
      </div>
    </div>
  );
}
